import { zodResolver } from "@hookform/resolvers/zod";
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
} from "@mui/material";
import { X } from "lucide-react";
import * as React from "react";
import { FormProvider, useForm } from "react-hook-form";

import {
  CustomFieldRecord,
  CustomFieldType,
} from "@/shared/domain/customFields";
import { useUpdateCustomField } from "@/shared/domain/customFields/hooks";
import { Button } from "@/shared/foundation/primitives";
import { RobotoDomainException } from "@/shared/http";

import styles from "./customFieldDialog.module.css";
import {
  buildUpdateCustomFieldRequest,
  EditCustomFieldFormValues,
  editFormDefaults,
  makeEditCustomFieldFormSchema,
} from "./editCustomFieldForm";
import { EnumOptionsEditor } from "./EnumOptionsEditor";

interface EditCustomFieldDialogProps {
  // The field to edit; `null` keeps the dialog closed.
  field: CustomFieldRecord | null;
  onClose: () => void;
}

const EMPTY_VALUES: EditCustomFieldFormValues = {
  display_name: "",
  description: "",
  enum_values: [],
};

export function EditCustomFieldDialog({
  field,
  onClose,
}: EditCustomFieldDialogProps) {
  const updateCustomField = useUpdateCustomField();
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const formSchema = React.useMemo(
    () => makeEditCustomFieldFormSchema(field),
    [field],
  );

  const form = useForm<EditCustomFieldFormValues>({
    defaultValues: EMPTY_VALUES,
    mode: "onChange",
    resolver: zodResolver(formSchema),
  });

  const { reset } = form;
  React.useEffect(() => {
    if (field) {
      reset(editFormDefaults(field));
      setSubmitError(null);
    }
  }, [field, reset]);

  const onSubmit = form.handleSubmit(async (values) => {
    if (!field) {
      return;
    }
    const request = buildUpdateCustomFieldRequest(values, field);
    // Nothing changed after trimming — nothing to send.
    if (Object.keys(request).length === 0) {
      onClose();
      return;
    }
    setSubmitError(null);
    try {
      await updateCustomField.mutateAsync({ fieldId: field.field_id, request });
      onClose();
    } catch (error) {
      let errorMessage = "Failed to save changes. Please try again.";
      if (error instanceof RobotoDomainException) {
        errorMessage = error.message;
      }
      setSubmitError(errorMessage);
    }
  });

  return (
    <Dialog
      className={styles.dialog}
      open={field !== null}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle className={styles.title}>
        Edit Field
        <IconButton aria-label="Close" size="small" onClick={onClose}>
          <X size={18} />
        </IconButton>
      </DialogTitle>
      <FormProvider {...form}>
        <form
          className={styles.form}
          noValidate
          onSubmit={(event) => void onSubmit(event)}
        >
          <DialogContent dividers className={styles.content}>
            {submitError ? <Alert severity="error">{submitError}</Alert> : null}

            <div className={styles.field}>
              <span className={styles.fieldLabel}>Field Name</span>
              <TextField
                className={styles.readonlyValue}
                size="small"
                fullWidth
                disabled
                value={field?.field_name ?? ""}
                helperText="Field name cannot be changed after creation."
              />
            </div>

            <div className={styles.field}>
              <span className={styles.fieldLabel}>Display Name (optional)</span>
              <TextField
                placeholder={field?.field_name ?? "e.g. Firmware Version"}
                size="small"
                fullWidth
                error={Boolean(form.formState.errors.display_name)}
                helperText={
                  form.formState.errors.display_name?.message ??
                  "Defaults to the field name when left blank."
                }
                {...form.register("display_name")}
              />
            </div>

            <div className={styles.field}>
              <span className={styles.fieldLabel}>Description (optional)</span>
              <TextField
                placeholder="Describe what this field is used for…"
                size="small"
                fullWidth
                multiline
                minRows={3}
                error={Boolean(form.formState.errors.description)}
                helperText={form.formState.errors.description?.message}
                {...form.register("description")}
              />
            </div>

            {field?.options?.field_type === CustomFieldType.Enum ? (
              <EnumOptionsEditor existingValues={field.options.enum_values} />
            ) : null}
          </DialogContent>
          <DialogActions className={styles.actions}>
            <Button variant="outlined" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              loading={form.formState.isSubmitting}
              disabled={
                !form.formState.isDirty ||
                !form.formState.isValid ||
                form.formState.isSubmitting
              }
            >
              Save Changes
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
}
